<template>
    <!-- Golden complete logo — a celebratory mark for the speedtest
         completion sequence. A gold disc carries a stroked check; a
         glowing aura halo breathes once behind it, and a thin gold ring
         draws itself in around the disc. The whole mark composes the
         enter choreography: aura bloom → disc scale-in → ring sweep →
         check stroke-draw, so the completion reads as a single polished
         reveal rather than a hard pop.

         The gold reads the shared `--color-gold` canon in
         `styles/tokens.css` — one source for every celebratory-gold
         consumer. The check stroke-draw rides a `keyframes.js`-free
         pure-CSS `stroke-dashoffset` animation; the aura is glass-ui's
         `<Pulse>`. -->
    <div
        class="complete-badge glass-material glass-refract"
        :class="{ 'is-shown': shown, 'is-personal-best': personalBest }"
        role="img"
        aria-label="Speedtest complete"
    >
        <!-- Celebration peak reads as gold-mark + check; gold-shimmer headline + Pulse aura on the surrounding chassis already name the celebration system (retired per AK-W5-β / G-AK-D1) -->
        <!-- Ambient gold halo — one-shot vivid breath behind the disc. -->
        <Pulse
            variant="aura"
            intensity="vivid"
            once
            class="complete-badge-aura"
            aria-hidden="true"
        />
        <!-- Gold disc + self-drawing ring + check glyph. -->
        <svg class="complete-badge-mark" viewBox="0 0 64 64" aria-hidden="true">
            <!-- Filled gold disc. -->
            <circle class="badge-disc" cx="32" cy="32" r="26" />
            <!-- Self-drawing gold ring framing the disc. -->
            <circle class="badge-ring" cx="32" cy="32" r="29" />
            <!-- B2 personal-best gold arc — an extra outer arc that sweeps
                 in ONLY when this run beats the stored best. The easter-egg
                 garnish: a triumphant second ring stroke in the gold
                 register. PRM-safe (the sweep collapses to a static drawn
                 arc), AA-large contrast (gold-light on the medallion). -->
            <circle
                v-if="personalBest"
                class="badge-best-arc"
                cx="32"
                cy="32"
                r="31"
            />
            <!-- Check glyph — stroke-draws on reveal. DDR-W22-HERO
                 ADJUDICATION: the check stays the clean pure-CSS
                 `stroke-dashoffset` draw, NOT glass-ui 3.10's HandMark.
                 The seal is a PRECISION instrument mark (it co-anchors the
                 tabular Fast.com number); a hand-sketched pen/crayon wobble
                 would clash with that register. See DDR-W22-HERO §Seal. -->
            <path
                class="badge-check"
                d="M20 33.5 L28.5 42 L45 24"
                fill="none"
            />
        </svg>
    </div>
</template>

<script setup lang="ts">
import { Pulse } from "@mkbabb/glass-ui/pulse";

defineProps<{
    /** When true, the badge plays its enter choreography. The consumer
     *  flips this on the completion rising edge. */
    shown: boolean;
    /** B2 — when true, this run beat the stored personal best; the badge
     *  sweeps an extra triumphant gold arc (the easter-egg garnish). The
     *  consumer (SpeedtestResults) resolves this off `state/personal-best`
     *  on the completion edge. Defaults false. */
    personalBest?: boolean;
}>();
</script>

<style scoped>
/* Celebratory gold reads the shared `--color-gold` canon (styles/tokens.css)
   — the same family the "Complete!" headline and the phase-timeline hover
   glow use, so the gold register has one source. */
.complete-badge {
    position: relative;
    display: grid;
    place-items: center;
    /* W22.1 hero seal (scope §3.2). The seal is the celebration
       choreography CLIMAX — promoted to HERO scale so the gold medallion
       reads as the confirmation lockup's optical anchor beside the
       demoted "Complete!" word, ONE size register with it. The prior
       ceiling (6.5rem) co-anchored a 4rem word; the W22 lockup pairs the
       seal with the `--type-display-4` word (≤5.382rem), so the seal
       ceiling lifts to 7.5rem to stay the lockup's lead element. The
       floor (3.5rem) + cqi arm grow it on the narrow COLUMN layout where
       the mark stacks above the word; 10cqi of a ~320px panel stays well
       under the ceiling. Existing clamp grammar; no new token.

       `glass-material glass-refract` (the ONE liquid-refract locus,
       DESIGN-INTAKE-2 §4): the medallion is the single surface in the
       complete stage that opts into glass-ui's convex-lens refraction
       garnish. The `@supports (backdrop-filter: url(#glass-refract))`
       gate + PE-only rule live in the publisher (glass-refract.css); the
       garnish refracts the aurora wash THROUGH the gold disc's rounded
       edge. `isolation: isolate` keeps the refract + aura stacking local. */
    inline-size: clamp(3.5rem, 10cqi, 7.5rem);
    aspect-ratio: 1;
    flex-shrink: 0;
    isolation: isolate;
    border-radius: 50%;
}

/* Aura halo — gold breath behind the disc. W12-ζ — refine the
   one-shot burst peak so the celebration reads as "the room cheers"
   (Disney EXAGGERATION + SECONDARY ACTION + APPEAL). The peak opacity
   lifts from 0.7 → 0.85 (more vivid bloom at the burst apex) and the
   period shortens from 1.5s → 1.2s so the burst lands inside the same
   window as the badge's disc-in + ring-sweep stages (which together
   peak at ~780ms). PRM falls through glass-ui's Pulse contract. */
.complete-badge-aura {
    --pulse-aura-color: var(--color-gold-light);
    --pulse-aura-opacity-min: 0;
    --pulse-aura-opacity-max: 0.85;
    --animate-ambient-pulse-duration: 1.2s;
}

.complete-badge-mark {
    inline-size: 100%;
    block-size: 100%;
    overflow: visible;
    z-index: 1;
}

/* Gold disc — radial gradient gives the mark dimensional sheen. */
.badge-disc {
    fill: var(--color-gold);
    filter: drop-shadow(
        0 0.25rem 0.75rem
            color-mix(in srgb, var(--color-gold-dark) 55%, transparent)
    );
    transform-origin: 32px 32px;
    transform: scale(0);
}

/* Self-drawing ring around the disc — `stroke-dashoffset` sweep. */
.badge-ring {
    fill: none;
    stroke: var(--color-gold-light);
    stroke-width: 2.5;
    stroke-linecap: round;
    /* circumference = 2π·29 ≈ 182.2 */
    stroke-dasharray: 182.2;
    stroke-dashoffset: 182.2;
    transform-origin: 32px 32px;
    transform: rotate(-90deg);
}

/* Check glyph — white stroke, draws itself on reveal. */
.badge-check {
    stroke: #fffaf0;
    stroke-width: 5.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    /* path length ≈ 47 */
    stroke-dasharray: 47;
    stroke-dashoffset: 47;
}

/* B2 — personal-best gold arc. An extra outer arc (r=31, OUTSIDE the
   r=29 ring) that sweeps in as a triumphant flourish ONLY when this run
   beats the stored best. It rides the gold-light register (AA-large on
   the medallion), and draws as a ~270° arc (a near-full sweep that reads
   as "a victory lap", visually distinct from the closed framing ring).
   The arc is `aria-hidden` via its decorative parent SVG; the
   personal-best fact is announced separately in the live region. */
.badge-best-arc {
    fill: none;
    stroke: var(--color-gold-light);
    stroke-width: 2;
    stroke-linecap: round;
    /* circumference = 2π·31 ≈ 194.8; render a ~270° arc (¾ sweep) by
       dashing 146.1 visible (¾·194.8) then the gap, and offsetting the
       full visible length so it draws ON during the sweep. */
    stroke-dasharray: 146.1 194.8;
    stroke-dashoffset: 146.1;
    transform-origin: 32px 32px;
    /* start the sweep from the top, rotated a touch off the ring so the
       two strokes read as distinct concentric marks. */
    transform: rotate(-100deg);
    opacity: 0.95;
}

/* ── Enter choreography — staged reveal ──────────────────────────────
   The four lanes fire on the `is-shown` flip with cascading delays so
   the mark assembles: disc scales in → ring sweeps → check draws. The
   aura's one-shot breath is owned by <Pulse once>. */
.complete-badge.is-shown .badge-disc {
    animation: badge-disc-in var(--motion-duration-badge-disc)
        var(--motion-ease-overshoot) var(--motion-delay-badge-disc) forwards;
}
.complete-badge.is-shown .badge-ring {
    animation: badge-ring-sweep var(--motion-duration-badge-ring)
        var(--motion-ease-staged-sweep) var(--motion-delay-badge-ring) forwards;
}
.complete-badge.is-shown .badge-check {
    animation: badge-check-draw var(--motion-duration-badge-check)
        var(--motion-ease-staged-draw) var(--motion-delay-badge-check) forwards;
}

/* B2 — the personal-best arc sweeps in AFTER the check finishes (the
   check ends ≈ 460ms delay + 380ms draw = ~840ms), so the gold arc is
   the LAST beat — the triumphant flourish. The keyframe is scoped-local
   (single consumer; Vue scopes the keyframe name too), so this egg adds
   nothing to the shared `@design/animations.css` registry. */
.complete-badge.is-shown.is-personal-best .badge-best-arc {
    animation: badge-best-arc-sweep 620ms var(--motion-ease-staged-sweep) 880ms
        forwards;
}
@keyframes badge-best-arc-sweep {
    from {
        stroke-dashoffset: 146.1;
        opacity: 0;
    }
    to {
        stroke-dashoffset: 0;
        opacity: 0.95;
    }
}

/* The `badge-disc-in` / `badge-ring-sweep` / `badge-check-draw` keyframes
   are hoisted to the central registry at `@design/animations.css` (W3.b);
   the `animation:` shorthands above resolve to those global names. */

/* Reduced-motion — the mark appears fully drawn, no staged motion.
   Static end-state so the celebratory mark still reads. */
@media (prefers-reduced-motion: reduce) {
    .complete-badge .badge-disc,
    .complete-badge .badge-ring,
    .complete-badge .badge-check,
    .complete-badge .badge-best-arc {
        animation: none;
    }
    .complete-badge .badge-disc {
        transform: scale(1);
    }
    .complete-badge .badge-ring,
    .complete-badge .badge-check {
        stroke-dashoffset: 0;
    }
    /* B2 PRM — the gold arc appears already drawn (the triumphant flourish
       reads as a static victory mark, no sweep). */
    .complete-badge .badge-best-arc {
        stroke-dashoffset: 0;
        opacity: 0.95;
    }
}
</style>
